/**
 * EmptyState - Empty state display for the Projects page.
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Plus, LucideIcon } from 'lucide-react';
import { Button, IconContainer } from '@/components/ui';
import type { ProjectConfig } from '@/lib/types';

interface EmptyStateProps {
  icon: LucideIcon;
  searchQuery: string;
  selectedProject: ProjectConfig | null;
  selectedDocType: string | null;
}

export const EmptyState: React.FC<EmptyStateProps> = ({
  icon: Icon,
  searchQuery,
  selectedProject,
  selectedDocType,
}) => {
  const { t } = useTranslation();

  const getMessage = () => {
    if (searchQuery) {
      return t('projects.emptySearchMessage');
    }
    if (selectedProject && selectedDocType) {
      return t('projects.emptyProjectAndType', { docType: selectedDocType, projectName: selectedProject.projectName });
    }
    if (selectedDocType) {
      return t('projects.emptyDocType', { docType: selectedDocType });
    }
    if (selectedProject) {
      return t('projects.emptyProject', { projectName: selectedProject.projectName });
    }
    return t('projects.emptyDefault');
  };

  return (
    <div className="text-center py-12 sm:py-16 px-4 bg-white rounded-xl border border-slate-200">
      <IconContainer variant="default" size="lg" className="mx-auto mb-4">
        <Icon className="w-7 h-7 sm:w-8 sm:h-8" />
      </IconContainer>
      <h3 className="text-lg sm:text-xl font-semibold text-slate-900 mb-2">
        {searchQuery ? t('projects.noDocumentsFound') : t('projects.noDocumentsYet')}
      </h3>
      <p className="text-sm sm:text-base text-slate-600 mb-6 max-w-md mx-auto">
        {getMessage()}
      </p>
      {!searchQuery && (
        <Button asChild variant="primary" size="lg" shape="rounded">
          <Link to="/add">
            <Plus className="w-4 h-4 sm:w-5 sm:h-5" />
            {t('projects.createDocument')}
          </Link>
        </Button>
      )}
    </div>
  );
};

export default EmptyState;
